import { Component } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Best-effort error reporter. Uses sendBeacon when available so the
 * report lands even if the page is being closed; falls back to fetch
 * with keepalive. Never throws — error reporting must not cascade into
 * yet another React error.
 *
 * iter-155 — added so owners see every production crash in the dashboard
 * without users needing to paste anything.
 */
function reportErrorToBackend(payload) {
  try {
    const body = JSON.stringify(payload);
    const url = `${API}/errors/report`;
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "include",
    }).catch(() => { /* swallow — reporting failure is non-fatal */ });
  } catch (e) { void e; /* never let the reporter itself crash */ }
}

/**
 * App-wide error boundary.
 *
 * Without this, ANY uncaught React error (render exception, useEffect throw,
 * unhandled promise rejection inside a sync render path) crashes the entire
 * React tree and the user sees only the platform's "Made with Emergent"
 * badge over a blank gradient. Production users reported this hitting the
 * Make Music flow during/after Suno playback.
 *
 * Strategy:
 *   - Render-time error → catch + show recovery UI (Retry / Reload buttons)
 *     instead of unmounting the world.
 *   - Capture the error + componentStack so we can surface it via toast
 *     and copy-to-clipboard for the user to paste back to support.
 *   - Auto-snapshot to console + window.__lastReactError so the existing
 *     consoleTail / Support Bot pipelines pick it up.
 *   - iter-154 — crash-loop auto-recovery: if 3+ errors hit within 60 s,
 *     start a 5 s "auto-reload in N…" countdown so users in a crash loop
 *     get rescued automatically. Cancellable with a button.
 *
 * iter-153 — added in response to a recurring production blank-page bug.
 */
const CRASH_LOOP_WINDOW_MS = 60_000;
const CRASH_LOOP_THRESHOLD = 3;
const AUTO_RELOAD_COUNTDOWN_S = 5;
const CRASH_LOOP_STORAGE_KEY = "elpm:err-boundary:recent-crashes";

function getRecentCrashes() {
  try {
    const raw = sessionStorage.getItem(CRASH_LOOP_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const cutoff = Date.now() - CRASH_LOOP_WINDOW_MS;
    return arr.filter((t) => typeof t === "number" && t > cutoff);
  } catch (e) { void e; return []; }
}

function recordCrash() {
  try {
    const now = Date.now();
    const recent = getRecentCrashes();
    recent.push(now);
    sessionStorage.setItem(CRASH_LOOP_STORAGE_KEY, JSON.stringify(recent));
    return recent.length;
  } catch (e) { void e; return 1; }
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      info: null,
      crashCount: 0,
      autoReloadSecs: null, // null = no auto-reload pending; integer = countdown
    };
    this.countdownTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const crashCount = recordCrash();
    this.setState({ info, crashCount });
    // Echo to console so installConsoleTail() captures it for /status + support reports.
    console.error("[ErrorBoundary] uncaught React error:", error, info?.componentStack);
    try {
      window.__lastReactError = {
        message: error?.message || String(error),
        stack: error?.stack || "",
        componentStack: info?.componentStack || "",
        at: new Date().toISOString(),
        crashCountInWindow: crashCount,
      };
    } catch (e) { void e; }
    // iter-155 — POST to /api/errors/report so the owner can see every
    // production crash in the dashboard without users needing to paste
    // details. Best-effort + rate-limited server-side (10/min/IP).
    try {
      let userToken = "";
      try { userToken = localStorage.getItem("elpm:user-token") || ""; } catch (e) { void e; }
      reportErrorToBackend({
        message: error?.message || String(error),
        stack: error?.stack || "",
        component_stack: info?.componentStack || "",
        url: typeof window !== "undefined" ? window.location.href : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        user_token: userToken,
        crash_count_in_window: crashCount,
        context: {
          react_error: true,
          last_action: (typeof window !== "undefined" && window.__lastUserAction) || null,
        },
      });
    } catch (e) { void e; }
    // iter-154 — if we've crashed CRASH_LOOP_THRESHOLD+ times in the
    // sliding window, start an auto-reload countdown the user can cancel.
    if (crashCount >= CRASH_LOOP_THRESHOLD) {
      this.startAutoReload();
    }
  }

  startAutoReload = () => {
    if (this.countdownTimer) return; // already counting down
    this.setState({ autoReloadSecs: AUTO_RELOAD_COUNTDOWN_S });
    this.countdownTimer = setInterval(() => {
      this.setState((prev) => {
        if (prev.autoReloadSecs == null) return prev;
        if (prev.autoReloadSecs <= 1) {
          this.handleReload();
          return prev;
        }
        return { ...prev, autoReloadSecs: prev.autoReloadSecs - 1 };
      });
    }, 1000);
  };

  cancelAutoReload = () => {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.setState({ autoReloadSecs: null });
  };

  componentWillUnmount() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  handleRetry = () => {
    this.cancelAutoReload();
    this.setState({ error: null, info: null });
  };

  handleReload = () => {
    try {
      // Clear the crash log so we don't loop straight into another reload
      // after the user manually loads back into a working state.
      sessionStorage.removeItem(CRASH_LOOP_STORAGE_KEY);
    } catch (e) { void e; }
    try { window.location.reload(); } catch (e) { void e; }
  };

  handleCopy = async () => {
    const { error, info, crashCount } = this.state;
    const payload = [
      `ELPM Studio · React error`,
      `Time: ${new Date().toISOString()}`,
      `URL: ${typeof window !== "undefined" ? window.location.href : ""}`,
      `Crashes in last 60 s: ${crashCount}`,
      ``,
      `Message: ${error?.message || String(error)}`,
      ``,
      `Stack:`,
      error?.stack || "(no stack)",
      ``,
      `Component stack:`,
      info?.componentStack || "(no component stack)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(payload);
    } catch (e) { void e; /* clipboard blocked; user can still read the inline pre */ }
  };

  render() {
    if (!this.state.error) return this.props.children;

    const msg = this.state.error?.message || String(this.state.error);
    const stack = this.state.error?.stack || "";
    const componentStack = this.state.info?.componentStack || "";
    const { autoReloadSecs, crashCount } = this.state;

    return (
      <div
        data-testid="error-boundary"
        className="fixed inset-0 z-[100] grid place-items-center bg-gradient-to-b from-zinc-950 to-black p-6 text-white"
      >
        <div className="grid w-full max-w-2xl gap-4 rounded-3xl border border-rose-400/30 bg-zinc-950/90 p-6 shadow-[0_30px_80px_-20px_rgba(244,63,94,0.5)]">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-rose-500 to-amber-400 text-2xl">!</div>
            <div>
              <h2 className="font-display text-xl font-bold">Something crashed</h2>
              <p className="font-mono text-[10px] uppercase tracking-wider text-rose-300/80">
                Your work in other panels is still there — try Retry first.
              </p>
            </div>
          </div>

          {autoReloadSecs != null && (
            <div
              data-testid="error-boundary-autoreload-banner"
              className="flex items-center justify-between gap-3 rounded-xl border border-amber-400/40 bg-amber-500/10 px-3 py-2"
            >
              <p className="font-mono text-[11px] uppercase tracking-wider text-amber-100">
                Crashed {crashCount}× recently · auto-reloading in
                <span data-testid="error-boundary-autoreload-secs" className="ml-1 font-bold text-amber-200">{autoReloadSecs}s</span>
              </p>
              <button
                type="button"
                data-testid="error-boundary-cancel-autoreload"
                onClick={this.cancelAutoReload}
                className="rounded-full border border-amber-400/50 bg-amber-500/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-amber-100 hover:bg-amber-500/30"
              >
                Cancel
              </button>
            </div>
          )}

          <div className="rounded-xl border border-white/10 bg-black/40 p-3">
            <p className="font-mono text-[10px] uppercase tracking-wider text-white/45">Error</p>
            <p data-testid="error-boundary-message" className="mt-1 break-words font-mono text-xs text-rose-200">{msg}</p>
          </div>

          <details className="rounded-xl border border-white/10 bg-black/40 p-3 text-[10px]" data-testid="error-boundary-details">
            <summary className="cursor-pointer font-mono uppercase tracking-wider text-white/55">Technical details (click)</summary>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] text-white/70">{stack}</pre>
            {componentStack && (
              <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] text-white/50">{componentStack}</pre>
            )}
          </details>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="error-boundary-retry"
              onClick={this.handleRetry}
              className="flex-1 rounded-xl bg-gradient-to-r from-fuchsia-500 to-amber-500 px-4 py-2 font-display text-sm font-semibold text-white shadow-[0_0_18px_rgba(232,121,249,0.4)] hover:opacity-90"
            >
              Try again
            </button>
            <button
              type="button"
              data-testid="error-boundary-reload"
              onClick={this.handleReload}
              className="rounded-xl border border-white/15 bg-white/[0.05] px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-white/75 hover:bg-white/[0.10]"
            >
              Reload page
            </button>
            <button
              type="button"
              data-testid="error-boundary-copy"
              onClick={this.handleCopy}
              className="rounded-xl border border-cyan-400/30 bg-cyan-500/[0.10] px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-cyan-100 hover:bg-cyan-500/[0.20]"
            >
              Copy details
            </button>
          </div>

          <p className="font-mono text-[10px] uppercase tracking-wider text-white/40">
            If this keeps happening, copy the details above and paste them to support so we can ship a fix.
          </p>
        </div>
      </div>
    );
  }
}
